import { Link } from "react-router-dom";
import type { CatalogItem, InventoryBalance } from "../../api/types";
import { formatInventoryUnitPrice } from "../../utils/formatInventoryUnitPrice";
import {
  formatQty,
  productCostView,
  type InventoryProductRow,
} from "../../utils/inventoryProducts";
import { COST_UNKNOWN } from "../../utils/unknownCost";
import { movesByBatch } from "../../utils/movesByBatch";
import { ItemThumb } from "../ItemThumb";

/**
 * G-94 — what opens when you tap an item in the stock list.
 *
 * It grew out of G-92's `InventoryActionSheet`, which only ever opened for a
 * row the shop already held: the "not in this shop yet" rows were plain divs
 * with nothing behind them, and the line under them told you to go and receive
 * the thing "from the shop page" — the page this work order retires.
 *
 * Owner ask 2026-09-17: *"when user clicks on it, it should open the item card
 * like it's done in Kiosk 'See stock' page."* It is NOT the kiosk component.
 * `ShopFloorItemCard` is 1,052 lines against eight floor-plane endpoints behind
 * `FloorJwtGuard`; a CRM user holds a staff token, so every one of those calls
 * would refuse. Forking it to vary the plane is the thing this repo has a rule
 * against. What carries over is the SHAPE — what the item is, where it is, and
 * what you can do with it — built on staff routes that already exist.
 *
 * The one rule it borrows outright is the kiosk's, from G-86: an item the shop
 * does not carry leads with **Receive**. (G-132: one it does carry no longer
 * leads with a single Adjust — each placement in the list has its own.)
 */

/**
 * G-132: a stocked target is a PRODUCT (every placement of one item), not one
 * balance row — the list shows one row per product, so the card answers for the
 * product and Adjust moves to each placement inside it.
 */
export type InventoryCardTarget =
  | { kind: "stocked"; product: InventoryProductRow }
  | { kind: "not-stocked"; item: CatalogItem };

export function InventoryItemCard({
  target,
  shopId,
  shopName,
  onAdjust,
  onReceive,
  onClose,
}: {
  target: InventoryCardTarget;
  /**
   * The shop in view, when one is chosen. Receiving needs a shop; in All-shops
   * mode an item with no rows has no honest answer to "receive it where?", so
   * the action says so instead of guessing (the G-94 toolbar rule).
   */
  shopId?: string;
  shopName?: string;
  onAdjust: (row: InventoryBalance) => void;
  /**
   * S2: when the host can receive here, the card opens that door in place.
   * Without it the card falls back to the shop page, which is what S1b shipped
   * and what the kiosk-free home panel still needs.
   */
  onReceive?: (preset: { itemId: string; itemName: string }) => void;
  onClose: () => void;
}) {
  const item = target.kind === "stocked" ? target.product.item : target.item;
  /** Every place THIS item is — a card answers "where is it?" once. Empty for an item the shop has none of. */
  const placements = target.kind === "stocked" ? target.product.placements : [];
  // WO-B part 6 (OD-198): the average speaks for the known units only.
  const cost = target.kind === "stocked" ? productCostView(target.product) : null;

  // Flowers and perishables move by batch, so their count is not hand-settable.
  // The same statement the table's Adjust button makes, and the server's.
  const canManualAdjust = !movesByBatch(item);

  // Receiving is done AT a shop: the one in view, else the only shop holding
  // the product. A product spread over several shops, seen in All shops, has
  // no honest answer to "receive it where?", so the action says so.
  const holdingShops = new Map(placements.map((p) => [p.location.id, p.location.name]));
  const onlyHolder = holdingShops.size === 1 ? [...holdingShops][0] : null;
  const receiveLocationId = shopId ?? onlyHolder?.[0];
  const receiveLocationName = shopId ? shopName : onlyHolder?.[1];

  const total = target.kind === "stocked" ? target.product.totalQty : 0;

  return (
    <div
      className="inv-sheet-backdrop"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="inv-sheet" role="dialog" aria-modal="true" aria-label={item.name}>
        <div className="inv-card-head">
          {/* `CatalogItem.imageKey` is optional, the balance row's is not —
              one shape for both, so the card does not care which it got. */}
          <ItemThumb itemId={item.id} imageKey={item.imageKey ?? null} alt="" size={56} />
          <div className="inv-card-head-text">
            <div className="inv-sheet-title">{item.name}</div>
            <div className="inv-sheet-sub">
              <span className="inv-card-tag">{item.category.label}</span>
              {item.color ? <span className="inv-card-tag">{item.color.name}</span> : null}
              <span className="inv-card-tag">{item.defaultUom}</span>
            </div>
          </div>
        </div>

        <dl className="inv-card-facts">
          <div>
            <dt>In stock</dt>
            <dd>
              {placements.length === 0 ? (
                <span className="inv-card-unknown">None in this shop yet</span>
              ) : (
                `${formatQty(total)} ${item.defaultUom}`
              )}
            </dd>
          </div>
          <div>
            <dt>Barcode</dt>
            <dd>
              {item.barcode ?? <span className="inv-card-unknown">Not scanned yet</span>}
            </dd>
          </div>
          {cost ? (
            <div>
              <dt>Unit cost</dt>
              <dd>
                {cost.unitCostEur != null ? (
                  formatInventoryUnitPrice(String(cost.unitCostEur), item.defaultUom)
                ) : (
                  /* G-30's rule: an unknown price is said, never invented. */
                  <span className="inv-card-unknown">{cost.costUnknown ? COST_UNKNOWN : "Not known"}</span>
                )}
                {cost.uncountedNote ? (
                  <small className="inv-card-cost-note">{cost.uncountedNote}</small>
                ) : null}
              </dd>
            </div>
          ) : null}
        </dl>

        {placements.length > 0 ? (
          <ul className="inv-card-zones">
            {placements.map((p) => (
              <li key={p.id}>
                <span>
                  {p.zone.name}
                  {p.location.name !== receiveLocationName ? ` · ${p.location.name}` : ""}
                </span>
                <span className="inv-card-zone-qty">
                  <b>{p.qtyOnHand}</b>
                  {/* G-132: Adjust is per placement — each zone has its own count. */}
                  <button
                    type="button"
                    className="button plain inv-card-zone-adjust"
                    disabled={!canManualAdjust}
                    title={
                      canManualAdjust
                        ? "Set on-hand quantity (audit logged)"
                        : "Flowers and perishables move by batch — use Receive, or waste and transfers from More actions"
                    }
                    onClick={() => onAdjust(p)}
                  >
                    Adjust
                  </button>
                </span>
              </li>
            ))}
          </ul>
        ) : null}

        {/* G-86's rule, on this plane: what the situation calls for goes first.
            An item the shop does not carry is nearly always one that just
            arrived, so Receive leads; one it carries leads with the count. */}
        {receiveLocationId && onReceive ? (
          /* S2: the receive door now opens on this page — no page change, and
             the list behind re-reads when it closes. */
          <button
            type="button"
            className={`inv-sheet-action${placements.length === 0 ? " is-primary" : ""}`}
            onClick={() => onReceive({ itemId: item.id, itemName: item.name })}
          >
            Receive stock
            <small>
              {receiveLocationName ? `Add what arrived at ${receiveLocationName}` : "Add what just arrived"}
            </small>
          </button>
        ) : receiveLocationId ? (
          /* S4: the shop page is retired, so this points at the inventory
             page directly rather than through its redirect. */
          <Link
            className={`inv-sheet-action${placements.length === 0 ? " is-primary" : ""}`}
            to={`/app/inventory?locationId=${encodeURIComponent(receiveLocationId)}&receiveForStock=${encodeURIComponent(item.id)}`}
          >
            Receive stock
            <small>
              {receiveLocationName ? `Add what arrived at ${receiveLocationName}` : "Add what just arrived"}
            </small>
          </Link>
        ) : (
          <button type="button" className="inv-sheet-action" disabled>
            Receive stock
            <small>Pick a shop first — receiving has to know where it lands</small>
          </button>
        )}

        <Link className="inv-sheet-action" to={`/app/items/${item.id}`}>
          Open item
          <small>Photo, price, barcode</small>
        </Link>

        <button type="button" className="inv-sheet-close" onClick={onClose}>
          Close
        </button>
      </div>
    </div>
  );
}
